import React, { useEffect, useState } from 'react';
import { Bot, Target, PenLine, Send, Linkedin, MessageCircle, CalendarCheck } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import api from '../../lib/api';

// "LEO handled today" — a prominent, always-visible snapshot of everything the
// autonomous engine did for the tenant today (resets at UTC midnight).
const METRICS = [
  { key: 'leads_scored',     label: 'Leads scored',   icon: Target,        color: 'text-sky-500',     bg: 'bg-sky-50 dark:bg-sky-950/40' },
  { key: 'emails_drafted',   label: 'Emails drafted', icon: PenLine,       color: 'text-indigo-500',  bg: 'bg-indigo-50 dark:bg-indigo-950/40' },
  { key: 'emails_sent',      label: 'Emails sent',    icon: Send,          color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/40' },
  { key: 'linkedin_actions', label: 'LinkedIn',       icon: Linkedin,      color: 'text-blue-500',    bg: 'bg-blue-50 dark:bg-blue-950/40' },
  { key: 'whatsapp_replies', label: 'WhatsApp',       icon: MessageCircle, color: 'text-teal-500',    bg: 'bg-teal-50 dark:bg-teal-950/40' },
  { key: 'meetings_booked',  label: 'Meetings',       icon: CalendarCheck, color: 'text-violet-500',  bg: 'bg-violet-50 dark:bg-violet-950/40' },
];

export const LeoHandledTodayCard = () => {
  const [state, setState] = useState({ loading: true, counts: null, total: 0 });

  useEffect(() => {
    let alive = true;
    api.get('/api/brain/leo-today')
      .then((r) => {
        if (!alive) return;
        const d = r.data || {};
        setState({ loading: false, counts: d.counts || {}, total: d.total_actions || 0 });
      })
      .catch(() => { if (alive) setState({ loading: false, counts: null, total: 0 }); });
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    return (
      <Card className="border-border/50 mt-4" data-testid="leo-today-loading">
        <CardContent className="p-5">
          <div className="h-4 w-40 bg-muted/50 rounded animate-pulse mb-4" />
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {METRICS.map((m) => (
              <div key={m.key} className="h-16 rounded-xl bg-muted/30 animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!state.counts) return null;

  return (
    <Card
      className="mt-4 overflow-hidden border-border/50 bg-gradient-to-br from-card to-indigo-50/30 dark:to-indigo-950/20"
      data-testid="leo-today-card"
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-500/10 flex items-center justify-center">
              <Bot className="h-5 w-5 text-indigo-500" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground leading-tight" data-testid="leo-today-title">
                LEO handled today
              </h3>
              <p className="text-xs text-muted-foreground">
                {state.total > 0
                  ? `${state.total} autonomous ${state.total === 1 ? 'action' : 'actions'} · resets at midnight`
                  : 'Waiting for the first action today · resets at midnight'}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {METRICS.map(({ key, label, icon: Icon, color, bg }) => {
            const value = state.counts[key] || 0;
            return (
              <div
                key={key}
                className={`rounded-xl border border-border/40 ${bg} p-3 flex flex-col items-center text-center transition-transform hover:-translate-y-0.5`}
                data-testid={`leo-today-${key}`}
              >
                <Icon className={`h-4 w-4 ${color} mb-1.5`} />
                <span className="text-xl font-bold text-foreground tabular-nums leading-none" data-testid={`leo-today-${key}-value`}>
                  {value}
                </span>
                <span className="mt-1 text-[11px] text-muted-foreground leading-tight">{label}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default LeoHandledTodayCard;
